import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ArrowLeft, DollarSign, Calendar, FileCheck, AlertCircle, CheckCircle, Loader2, Send, Lock, BadgeCheck, UserCheck, ShieldCheck, FileText, Scale } from "lucide-react";
import { toast } from "sonner";
import CredentialsVault from "../components/CredentialsVault";
import PayrollBreakdown from "../components/PayrollBreakdown";
import { WageUpLogo, WageUpLockup } from "../components/WageUpLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const usDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${m}/${d}/${y}`;
};

function mondayOf(d) {
  const dt = new Date(d + "T00:00:00");
  const day = dt.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  dt.setDate(dt.getDate() + diff);
  return dt.toISOString().slice(0, 10);
}

export default function PayrollPage() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(sessionStorage.getItem("master_authed") === "true");
  const [masterCode, setMasterCode] = useState(sessionStorage.getItem("master_code") || "");
  const [pin, setPin] = useState("");
  const [tab, setTab] = useState("run");
  const [weekStart, setWeekStart] = useState(mondayOf(new Date().toISOString().slice(0, 10)));
  const [preview, setPreview] = useState(null);
  const [preview1099, setPreview1099] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [providerStatus, setProviderStatus] = useState(null);
  const [calendar, setCalendar] = useState(null);
  const [history, setHistory] = useState([]);
  const [history1099, setHistory1099] = useState([]);

  useEffect(() => {
    if (authed) {
      loadProvider();
      loadHistory();
      loadCalendar();
    }
  }, [authed]);

  useEffect(() => {
    if (!authed) return;
    if (tab === "run") runPreview();
    if (tab === "run-1099") runPreview1099();
  }, [authed, weekStart, tab]);

  const loadProvider = async () => {
    try {
      const r = await axios.get(`${API}/payroll/provider-status`);
      setProviderStatus(r.data);
    } catch { /* ignore */ }
  };

  const loadHistory = async () => {
    try {
      const r = await axios.get(`${API}/payroll/list`);
      setHistory(r.data.pay_runs || []);
    } catch { /* ignore */ }
    try {
      const r2 = await axios.get(`${API}/payroll/list-1099`);
      setHistory1099(r2.data.pay_runs || []);
    } catch { /* ignore */ }
  };

  const loadCalendar = async () => {
    try {
      const r = await axios.get(`${API}/payroll/tax-calendar`);
      setCalendar(r.data);
    } catch { /* ignore */ }
  };

  const runPreview = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/payroll/preview`, { params: { week_start: weekStart } });
      setPreview(r.data);
    } catch (err) {
      toast.error("Preview failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const runPreview1099 = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/payroll/preview-1099`, { params: { week_start: weekStart } });
      setPreview1099(r.data);
    } catch (err) {
      toast.error("1099 preview failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handlePinSubmit = () => {
    if (["83528", "4817", "1111"].includes(pin)) {
      sessionStorage.setItem("master_authed", "true");
      sessionStorage.setItem("master_code", pin);
      setMasterCode(pin);
      setAuthed(true);
    } else {
      toast.error("Invalid master code");
    }
  };

  const submitPayRun = async () => {
    if (!preview) return;
    if (!providerStatus?.connected) {
      const ok = window.confirm("Patriot Software is not connected yet. This will save a draft only — no actual filings or payments will happen until you connect Patriot. Proceed?");
      if (!ok) return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/payroll/submit`, preview);
      toast.success("Pay run submitted");
      loadHistory();
    } catch (err) {
      toast.error("Submit failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  const submit1099Run = async () => {
    if (!preview1099) return;
    const ok = window.confirm("Submit 1099 contractor pay run? No taxes are withheld. ACH/checks must be issued manually until Patriot is connected.");
    if (!ok) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/payroll/submit-1099`, preview1099);
      toast.success("1099 pay run submitted");
      loadHistory();
    } catch (err) {
      toast.error("Submit failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-stone-100 via-stone-50 to-rose-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md border border-stone-100">
          <WageUpLockup logoSize={56} className="mb-6" />
          <div className="border-l-4 border-[#A61B1B] pl-3 mb-5">
            <p className="text-sm font-semibold text-stone-900">Payroll Suite Access</p>
            <p className="text-xs text-stone-500">Enter your master code to continue.</p>
          </div>
          <Input type="password" value={pin} onChange={(e) => setPin(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handlePinSubmit()} placeholder="••••" className="text-center text-2xl tracking-widest h-14" data-testid="payroll-pin-input" />
          <Button className="w-full mt-3 h-12 bg-[#A61B1B] hover:bg-[#841515]" onClick={handlePinSubmit} data-testid="payroll-pin-submit">Continue</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="bg-white border-b border-stone-200 px-4 py-2.5 flex items-center justify-between sticky top-0 z-10">
        <button onClick={() => navigate("/timecard")} className="flex items-center gap-2 text-stone-700 hover:text-[#A61B1B]" data-testid="payroll-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <WageUpLockup logoSize={32} />
        <div className="w-12"></div>
      </div>

      {/* Provider banner */}
      {providerStatus && !providerStatus.connected && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 text-sm flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
          <div className="flex-1">
            <span className="font-bold text-amber-900">Patriot Software not connected.</span>
            <span className="text-amber-800"> Pay run preview works fully. To actually file taxes + run direct deposit, sign up at patriotsoftware.com and add your API key.</span>
          </div>
        </div>
      )}
      {providerStatus?.connected && (
        <div className="bg-green-50 border-b border-green-200 px-4 py-2 text-sm flex items-center gap-2">
          <BadgeCheck className="w-4 h-4 text-green-700" />
          <span className="text-green-900 font-medium">Patriot Software connected. All filings & ACH are live.</span>
        </div>
      )}

      <div className="max-w-6xl mx-auto p-3 sm:p-4">
        {/* Tabs */}
        <div className="bg-white rounded-lg p-1 flex gap-1 mb-4 shadow-sm overflow-x-auto">
          {[
            { id: "run", label: "W-2 Payroll", icon: Send },
            { id: "run-1099", label: "1099 Contractors", icon: UserCheck },
            { id: "breakdown", label: "Full Breakdown", icon: Scale },
            { id: "vault", label: "Credentials Vault", icon: ShieldCheck },
            { id: "calendar", label: "Tax Calendar", icon: Calendar },
            { id: "history", label: "History", icon: FileCheck },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`flex-1 py-2.5 px-3 rounded-md font-semibold text-sm whitespace-nowrap transition ${tab === t.id ? "bg-[#A61B1B] text-white" : "text-stone-600 hover:bg-stone-100"}`}
            >
              <t.icon className="w-4 h-4 inline mr-1" /> {t.label}
            </button>
          ))}
        </div>

        {/* RUN PAYROLL (W-2) */}
        {tab === "run" && (
          <div className="space-y-4">
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <span className="text-sm font-semibold text-stone-700">W-2 Pay Period (Mon → Sun):</span>
                <Input type="date" value={weekStart} onChange={(e) => setWeekStart(mondayOf(e.target.value))} className="w-44" data-testid="pay-week-input" />
                {preview && <span className="text-sm font-mono text-stone-600">{usDate(preview.pay_period_start)} → {usDate(preview.pay_period_end)}</span>}
                {preview && <span className="ml-auto text-sm">Pay date: <span className="font-bold">{usDate(preview.pay_date)}</span></span>}
              </div>
              <p className="text-xs text-stone-500 italic">Cash-paid employees are off-books and never included here. They are paid via Kiosk punches.</p>
            </div>

            {loading ? (
              <div className="bg-white rounded-lg p-12 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /> Computing pay run…</div>
            ) : preview && preview.rows.length === 0 ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-8 text-center">
                <AlertCircle className="w-10 h-10 mx-auto mb-2 text-amber-600" />
                <p className="font-bold text-stone-900">No W-2 employees with hours this week</p>
                <p className="text-sm text-stone-600 mt-1">Either no schedule was built, or all scheduled employees are cash-paid (off-books).</p>
                <Button className="mt-3" variant="outline" onClick={() => navigate("/schedule")}>Open Schedule Builder</Button>
              </div>
            ) : preview && (
              <>
                {/* Summary card */}
                <div className="bg-white rounded-lg shadow-sm p-4">
                  <h2 className="font-bold text-base mb-3">Pay Run Summary</h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <SumTile label="Gross Wages" value={`$${preview.totals.gross.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`} color="#1A5E1F" />
                    <SumTile label="Net (Direct Deposit)" value={`$${preview.totals.net.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`} color="#A61B1B" />
                    <SumTile label="Tax Withholdings" value={`$${(preview.totals.fed_wh + preview.totals.il_wh + preview.totals.fica_ss + preview.totals.fica_medicare).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`} color="#92400E" />
                    <SumTile label="Employer Total Cost" value={`$${preview.totals.total_employer_cost.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`} color="#1A1A1A" />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-3 text-xs">
                    <div className="bg-stone-50 rounded p-2"><div className="text-stone-500">Federal W/H</div><div className="font-bold">${preview.totals.fed_wh.toLocaleString()}</div></div>
                    <div className="bg-stone-50 rounded p-2"><div className="text-stone-500">IL W/H</div><div className="font-bold">${preview.totals.il_wh.toLocaleString()}</div></div>
                    <div className="bg-stone-50 rounded p-2"><div className="text-stone-500">SS (employee)</div><div className="font-bold">${preview.totals.fica_ss.toLocaleString()}</div></div>
                    <div className="bg-stone-50 rounded p-2"><div className="text-stone-500">Medicare (employee)</div><div className="font-bold">${preview.totals.fica_medicare.toLocaleString()}</div></div>
                    <div className="bg-stone-50 rounded p-2"><div className="text-stone-500">Employer Match + SUI + FUTA</div><div className="font-bold">${(preview.totals.employer_fica + preview.totals.employer_sui + preview.totals.employer_futa).toFixed(2)}</div></div>
                  </div>
                  <p className="text-xs text-stone-500 mt-2 italic">{preview.tax_provider}</p>
                </div>

                {/* Per-employee table */}
                <div className="bg-white rounded-lg shadow-sm p-4">
                  <h3 className="font-bold text-sm mb-3">Pay Detail by Employee ({preview.totals.headcount} W-2 employees)</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-stone-100 text-xs">
                        <tr>
                          <th className="text-left px-2 py-1.5">Employee</th>
                          <th className="text-left px-2 py-1.5">Dept</th>
                          <th className="text-right px-2 py-1.5">Hours</th>
                          <th className="text-right px-2 py-1.5">Rate</th>
                          <th className="text-right px-2 py-1.5">Gross</th>
                          <th className="text-right px-2 py-1.5">Fed W/H</th>
                          <th className="text-right px-2 py-1.5">IL W/H</th>
                          <th className="text-right px-2 py-1.5">FICA</th>
                          <th className="text-right px-2 py-1.5 font-bold">Net Pay</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.rows.map(r => (
                          <tr key={r.employee_code} className="border-b border-stone-100 hover:bg-stone-50">
                            <td className="px-2 py-1.5 font-medium">{r.name} <span className="text-xs text-stone-400">#{r.employee_code}</span></td>
                            <td className="px-2 py-1.5 text-xs text-stone-600">{r.department}</td>
                            <td className="px-2 py-1.5 text-right font-mono text-xs">{r.salary_weekly > 0 ? <em>Salary</em> : `${(r.hours_regular + r.hours_overtime).toFixed(1)}h${r.hours_overtime > 0 ? ` (${r.hours_overtime.toFixed(1)} OT)` : ""}`}</td>
                            <td className="px-2 py-1.5 text-right font-mono text-xs">{r.salary_weekly > 0 ? `$${r.salary_weekly}/wk` : `$${r.hourly_rate}/h`}</td>
                            <td className="px-2 py-1.5 text-right font-mono">${r.gross_pay.toFixed(2)}</td>
                            <td className="px-2 py-1.5 text-right font-mono text-stone-600">-${r.fed_withholding.toFixed(2)}</td>
                            <td className="px-2 py-1.5 text-right font-mono text-stone-600">-${r.il_withholding.toFixed(2)}</td>
                            <td className="px-2 py-1.5 text-right font-mono text-stone-600">-${(r.fica_ss + r.fica_medicare).toFixed(2)}</td>
                            <td className="px-2 py-1.5 text-right font-mono font-bold">${r.net_pay.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm p-4 flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm text-stone-600">
                    {providerStatus?.connected
                      ? "Submitting will trigger ACH direct deposits + tax deposits + scheduled filings via Patriot."
                      : "Patriot not connected — submit will save a draft only."
                    }
                  </p>
                  <Button onClick={submitPayRun} disabled={submitting} className="bg-[#A61B1B] hover:bg-[#841515] h-12 px-6" data-testid="submit-pay-run">
                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    {providerStatus?.connected ? "Submit Payroll" : "Save Draft"}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {/* RUN 1099 CONTRACTORS */}
        {tab === "run-1099" && (
          <div className="space-y-4">
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <span className="text-sm font-semibold text-stone-700">1099 Pay Period (Mon → Sun):</span>
                <Input type="date" value={weekStart} onChange={(e) => setWeekStart(mondayOf(e.target.value))} className="w-44" data-testid="pay-1099-week-input" />
                {preview1099 && <span className="text-sm font-mono text-stone-600">{usDate(preview1099.pay_period_start)} → {usDate(preview1099.pay_period_end)}</span>}
                {preview1099 && <span className="ml-auto text-sm">Pay date: <span className="font-bold">{usDate(preview1099.pay_date)}</span></span>}
              </div>
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-1">
                <strong>1099-NEC contractors:</strong> NO federal/state withholding, NO FICA, NO FUTA. They handle their own self-employment taxes. We track YTD and issue Form 1099-NEC at year-end for any contractor ≥ $600.
              </p>
            </div>

            {loading ? (
              <div className="bg-white rounded-lg p-12 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /> Computing 1099 pay run…</div>
            ) : preview1099 && preview1099.rows.length === 0 ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-8 text-center">
                <AlertCircle className="w-10 h-10 mx-auto mb-2 text-amber-600" />
                <p className="font-bold text-stone-900">No 1099 contractors with hours this week</p>
                <p className="text-sm text-stone-600 mt-1">Mark contractors with pay_category = &quot;1099&quot; in Employee Setup to include them here.</p>
              </div>
            ) : preview1099 && (
              <>
                <div className="bg-white rounded-lg shadow-sm p-4">
                  <h2 className="font-bold text-base mb-3">1099 Pay Run Summary</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <SumTile label="Gross Pay (to ACH/check)" value={`$${preview1099.totals.gross.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`} color="#A61B1B" />
                    <SumTile label="Contractors Paid" value={`${preview1099.totals.headcount}`} color="#1A5E1F" />
                    <SumTile label="Need 1099-NEC at Year-End" value={`${preview1099.totals.needs_1099_count}`} color="#92400E" />
                  </div>
                  <p className="text-xs text-stone-500 mt-3 italic">{preview1099.tax_provider}</p>
                </div>

                <div className="bg-white rounded-lg shadow-sm p-4">
                  <h3 className="font-bold text-sm mb-3">Contractor Detail</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-stone-100 text-xs">
                        <tr>
                          <th className="text-left px-2 py-1.5">Contractor</th>
                          <th className="text-left px-2 py-1.5">Dept</th>
                          <th className="text-right px-2 py-1.5">Hours</th>
                          <th className="text-right px-2 py-1.5">Rate</th>
                          <th className="text-right px-2 py-1.5 font-bold">Gross (Net = Gross)</th>
                          <th className="text-right px-2 py-1.5">YTD Paid</th>
                          <th className="text-right px-2 py-1.5">YTD After Run</th>
                          <th className="text-center px-2 py-1.5">1099-NEC</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview1099.rows.map(r => (
                          <tr key={r.employee_code} className="border-b border-stone-100 hover:bg-stone-50">
                            <td className="px-2 py-1.5 font-medium">{r.name} <span className="text-xs text-stone-400">#{r.employee_code}</span></td>
                            <td className="px-2 py-1.5 text-xs text-stone-600">{r.department}</td>
                            <td className="px-2 py-1.5 text-right font-mono text-xs">{r.hours.toFixed(1)}h</td>
                            <td className="px-2 py-1.5 text-right font-mono text-xs">${r.hourly_rate}/h</td>
                            <td className="px-2 py-1.5 text-right font-mono font-bold">${r.gross_pay.toFixed(2)}</td>
                            <td className="px-2 py-1.5 text-right font-mono text-xs text-stone-600">${r.ytd_paid.toFixed(2)}</td>
                            <td className="px-2 py-1.5 text-right font-mono text-xs">${r.ytd_plus_this_run.toFixed(2)}</td>
                            <td className="px-2 py-1.5 text-center">
                              {r.needs_1099_nec
                                ? <span className="text-xs bg-amber-100 text-amber-900 px-2 py-0.5 rounded font-bold">REQUIRED</span>
                                : <span className="text-xs text-stone-400">under $600</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-stone-500 italic mt-2">1099-NEC required when YTD ≥ $600. We auto-generate at year-end.</p>
                </div>

                <div className="bg-white rounded-lg shadow-sm p-4 flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm text-stone-600">Submit will record this 1099 pay run. ACH/check disbursement must be done manually until Patriot is connected.</p>
                  <Button onClick={submit1099Run} disabled={submitting} className="bg-[#A61B1B] hover:bg-[#841515] h-12 px-6" data-testid="submit-1099-run">
                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    Submit 1099 Pay Run
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {/* CREDENTIALS VAULT */}
        {tab === "vault" && (
          <CredentialsVault masterCode={masterCode} />
        )}

        {/* FULL BREAKDOWN — strict cash/W-2/1099 separation */}
        {tab === "breakdown" && (
          <PayrollBreakdown initialWeek={weekStart} />
        )}

        {/* TAX CALENDAR */}
        {tab === "calendar" && calendar && (
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h2 className="font-bold text-base mb-1">Tax Filing Calendar — {calendar.year}</h2>
            <p className="text-sm text-stone-600 mb-3">
              {calendar.provider_connected
                ? `Filings auto-handled by ${calendar.provider}.`
                : "Provider not connected. These filings require manual handling until you sign up for Patriot."}
            </p>
            <div className="space-y-2">
              {calendar.items.map((it, i) => (
                <div key={i} className="flex items-center justify-between p-3 border border-stone-200 rounded">
                  <div>
                    <div className="font-semibold text-sm">{it.label}</div>
                    <div className="text-xs text-stone-500">Due: {usDate(it.due_date)}</div>
                  </div>
                  <StatusPill status={it.status} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* HISTORY */}
        {tab === "history" && (
          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h2 className="font-bold text-base mb-3">W-2 Pay Run History</h2>
              {history.length === 0 ? (
                <p className="text-sm text-stone-500">No W-2 pay runs yet.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-stone-100 text-xs">
                    <tr><th className="text-left px-2 py-1.5">Pay Period</th><th className="text-left px-2 py-1.5">Pay Date</th><th className="text-right px-2 py-1.5">Gross</th><th className="text-right px-2 py-1.5">Net</th><th className="text-right px-2 py-1.5">HC</th><th className="text-right px-2 py-1.5">Status</th></tr>
                  </thead>
                  <tbody>
                    {history.map(r => (
                      <tr key={r.id} className="border-b border-stone-100">
                        <td className="px-2 py-1.5">{usDate(r.pay_period_start)} → {usDate(r.pay_period_end)}</td>
                        <td className="px-2 py-1.5">{usDate(r.pay_date)}</td>
                        <td className="px-2 py-1.5 text-right font-mono">${(r.totals?.gross || 0).toLocaleString()}</td>
                        <td className="px-2 py-1.5 text-right font-mono font-bold">${(r.totals?.net || 0).toLocaleString()}</td>
                        <td className="px-2 py-1.5 text-right">{r.totals?.headcount || 0}</td>
                        <td className="px-2 py-1.5 text-right"><StatusPill status={r.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="bg-white rounded-lg shadow-sm p-4">
              <h2 className="font-bold text-base mb-3">1099 Contractor Pay Run History</h2>
              {history1099.length === 0 ? (
                <p className="text-sm text-stone-500">No 1099 pay runs yet.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-stone-100 text-xs">
                    <tr><th className="text-left px-2 py-1.5">Pay Period</th><th className="text-left px-2 py-1.5">Pay Date</th><th className="text-right px-2 py-1.5">Gross</th><th className="text-right px-2 py-1.5">HC</th><th className="text-right px-2 py-1.5">Status</th></tr>
                  </thead>
                  <tbody>
                    {history1099.map(r => (
                      <tr key={r.id} className="border-b border-stone-100">
                        <td className="px-2 py-1.5">{usDate(r.pay_period_start)} → {usDate(r.pay_period_end)}</td>
                        <td className="px-2 py-1.5">{usDate(r.pay_date)}</td>
                        <td className="px-2 py-1.5 text-right font-mono">${(r.totals?.gross || 0).toLocaleString()}</td>
                        <td className="px-2 py-1.5 text-right">{r.totals?.headcount || 0}</td>
                        <td className="px-2 py-1.5 text-right"><StatusPill status={r.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const SumTile = ({ label, value, color }) => (
  <div className="bg-stone-50 border border-stone-200 rounded-lg p-3">
    <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold">{label}</div>
    <div className="text-xl font-extrabold mt-1" style={{ color }}>{value}</div>
  </div>
);

const StatusPill = ({ status }) => {
  const map = {
    "auto-filed":      { color: "bg-green-100 text-green-900", icon: CheckCircle, label: "Filed ✓" },
    "scheduled":       { color: "bg-blue-100 text-blue-900", icon: Calendar, label: "Scheduled" },
    "manual-required": { color: "bg-amber-100 text-amber-900", icon: AlertCircle, label: "Manual" },
    "submitted":       { color: "bg-green-100 text-green-900", icon: CheckCircle, label: "Submitted" },
    "draft":           { color: "bg-stone-200 text-stone-700", icon: FileCheck, label: "Draft" },
  };
  const s = map[status] || { color: "bg-stone-100 text-stone-700", icon: FileCheck, label: status };
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded font-bold ${s.color}`}>
      <Icon className="w-3 h-3" /> {s.label}
    </span>
  );
};
